import React from 'react';
import { Box, Typography, Chip } from '@material-ui/core';
import { 
  Description, 
  Code, 
  CheckCircle, 
  Image,
  Assignment,
  Videocam
} from '@material-ui/icons';
import RichTextAnswer from './RichTextAnswer';
import ScenarioTextAnswer from './ScenarioTextAnswer';
import QuestionRenderer from './QuestionRenderer';
import VideoRecorder from './VideoRecorder';
import ImageUploadService from '../services/ImageUploadService';

const EnhancedQuestionRenderer = ({ 
  question, 
  answer, 
  onAnswerChange, 
  disabled = false,
  examCode,
  userId 
}) => {
  // Handle image uploads for rich text answers
  const handleImageUpload = async (images, questionId) => {
    try {
      // Upload images to Firebase Storage
      const uploadedImages = await ImageUploadService.uploadImages(
        images, 
        examCode, 
        questionId, 
        userId
      );

      // Update the answer with uploaded image URLs
      const updatedAnswer = {
        ...answer,
        images: uploadedImages
      };
      
      onAnswerChange(questionId, updatedAnswer);
      
      
    } catch (error) {
      throw error;
    }
  };

  // Get question type icon
  const getQuestionIcon = (type) => {
    switch (type) {
      case 'text':
      case 'scenario-text':
        return <Description />;
      case 'code':
        return <Code />;
      case 'mcq-single':
      case 'mcq-multi':
        return <CheckCircle />;
      case 'image':
        return <Image />;
      case 'video':
        return <Videocam />;
      default:
        return <Assignment />;
    }
  };

  // Get question type label
  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'text':
        return 'Text Answer';
      case 'scenario-text':
        return 'Scenario-Based Text';
      case 'code':
        return 'Code Answer';
      case 'mcq-single':
        return 'Single Choice';
      case 'mcq-multi':
        return 'Multiple Choice';
      case 'image':
        return 'Image Upload';
      case 'video':
        return 'Video Recording';
      default:
        return 'Question';
    }
  };

  // Render rich text answer for text questions
  if (question.type === 'text') {
    return (
      <RichTextAnswer
        question={question}
        answer={answer}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
        examCode={examCode}
        userId={userId}
      />
    );
  }

  // Render scenario-text questions
  if (question.type === 'scenario-text') {
    return (
      <ScenarioTextAnswer
        question={question}
        answer={answer}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
        examCode={examCode}
        userId={userId}
      />
    );
  }

  // Render video recording questions
  if (question.type === 'video') {
    return (
      <VideoRecorder
        question={question}
        answer={answer}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
        examCode={examCode}
        userId={userId}
      />
    );
  }

  // For other question types, use the existing QuestionRenderer
  return (
    <Box className="enhanced-question-renderer">
      <Box className="question-header">
        <Box className="question-type-indicator">
          {getQuestionIcon(question.type)}
          <Typography variant="subtitle2" color="textSecondary">
            {getQuestionTypeLabel(question.type)}
          </Typography>
        </Box>
        {question.maxScore && (
          <Chip 
            label={`${question.maxScore} points`} 
            color="primary" 
            size="small" 
          />
        )}
      </Box>

      <QuestionRenderer
        question={question}
        answer={answer}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default EnhancedQuestionRenderer; 